import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, ChevronDown, Cloud, Plus, Trash2, ArrowLeft, Server, Activity, Globe, Terminal, RotateCw, HardDrive, Database, Search, X, FileCode, Save, Folder, FolderOpen, File, Settings, User, Sparkles, Code, Wand2, Megaphone, Play, Square, Package, UploadCloud, Github, LayoutTemplate, ExternalLink, AlertTriangle, Camera, Cpu, Zap, Shield, Users, CheckCircle } from 'lucide-react';
import api, { API_BASE } from '../services/api';
import DeploymentCard from "../components/deploy/DeploymentCard";
import DeploymentDetailPage from "../components/deploy/DeploymentDetailPage";
import copyToClipboard from '../utils/clipboard';
import Deploy from './Deploy';
import ContainerTerminal from '../components/ContainerTerminal';
import ComposeWorkspaceModal from '../components/ComposeWorkspaceModal';
import '../styles/dashboard.css';
const formatBytesValue = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(k)), sizes.length - 1);
  return `${Math.round((value / Math.pow(k, index)) * 100) / 100} ${sizes[index]}`;
};

const parseMemoryToBytes = (memStr) => {
  if (!memStr) return 0;
  if (typeof memStr === 'number') {
    if (memStr <= 128) return Math.round(memStr * (1024 ** 3));
    if (memStr <= 131072) return Math.round(memStr * (1024 ** 2));
    return Math.round(memStr);
  }
  const match = String(memStr).trim().match(/^([0-9.]+)\s*([a-z]*)$/i);
  if (!match) return parseInt(memStr, 10) || 0;
  const value = parseFloat(match[1]);
  let unit = match[2].toLowerCase();
  if (!unit) {
    if (value <= 128) unit = 'g';
    else if (value <= 131072) unit = 'm';
  }
  const multipliers = { 'b':1, '':1, 'k':1024, 'kb':1024, 'm':1024**2, 'mb':1024**2, 'g':1024**3, 'gb':1024**3, 't':1024**4, 'tb':1024**4 };
  return Math.round(value * (multipliers[unit] || 1));
};







function Dashboard({ user, onStatusMessage, onConfirm, activeTab = 'deployments', onDeployRequested, openGlobalWorkspace }) {
  const [selectedDeploymentPath, setSelectedDeploymentPath] = useState(null);
  const [apps, setApps] = useState({});
  const [adminProfile, setAdminProfile] = useState(null);


  const [selectedDeveloper, setSelectedDeveloper] = useState(null);
  const [copiedPath, setCopiedPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDeployments, setActiveDeployments] = useState([]);
  const dismissedDeploymentIds = useRef(new Set());
  const [developers, setDevelopers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [embeddedDeployActiveTab, setEmbeddedDeployActiveTab] = useState(null);

  const handleStartDeploy = (tab) => {
    if (onDeployRequested) {
      onDeployRequested(tab);
    } else {
      setEmbeddedDeployActiveTab(tab);
    }
  };
  const [domainEdits, setDomainEdits] = useState({});
  const [editingDomain, setEditingDomain] = useState({});
  const [domainSaving, setDomainSaving] = useState({});
  const [deploymentLogSnapshots, setDeploymentLogSnapshots] = useState({});
  const [deploymentRuntimeLogs, setDeploymentRuntimeLogs] = useState({});
  const [deploymentRuntimeLoading, setDeploymentRuntimeLoading] = useState({});
  const [expandedLogs, setExpandedLogs] = useState({});
  const [showFileEditor, setShowFileEditor] = useState(false);
  const [editingFileData, setEditingFileData] = useState({ appName: '', owner: '', filename: '', content: '', originalContent: '', loading: false, saving: false, error: null });
  const [fileAiState, setFileAiState] = useState({ prompt: '', messages: [], loading: false, showSidebar: false });
  const [fileEditorShowTerminal, setFileEditorShowTerminal] = useState(true);
  const [fileSearch, setFileSearch] = useState({ visible: false, query: '', matches: [], currentIndex: -1 });
  const [showDeployTypeModal, setShowDeployTypeModal] = useState(false);
  const [fileBrowser, setFileBrowser] = useState({ entries: [], loading: false, currentDir: '', expandedDirs: {}, dirCache: {} });
  const [activeIdeDropdown, setActiveIdeDropdown] = useState(null);
  const [creatingFileIn, setCreatingFileIn] = useState(null);
  const [newFileName, setNewFileName] = useState('');
  const [showTerminal, setShowTerminal] = useState(false);
  const [sshCredPopup, setSshCredPopup] = useState(null);
  const [terminalApp, setTerminalApp] = useState({ appName: '', owner: '', mode: 'shell', command: '/bin/sh', sshConfig: null });
  const [editingAppConfig, setEditingAppConfig] = useState(null);
  const [composeModalApp, setComposeModalApp] = useState(null);
  const [composeModalAppType, setComposeModalAppType] = useState('compose');
  const [composeModalInfo, setComposeModalInfo] = useState(null);
  const textareaRef = useRef(null);
  const deploymentLogSnapshotsRef = useRef({});
  const [statsTab, setStatsTab] = useState('overview');

  const [globalAnnouncement, setGlobalAnnouncement] = useState({ active: false, message: '' });

  useEffect(() => {
    fetch(`${API_BASE}/announcements`)
      .then(res => res.json())
      .then(data => {
        if (data && data.active) {
          setGlobalAnnouncement(data);
        }
      })
      .catch(err => console.error('Failed to load announcements:', err));
  }, []);

  useEffect(() => {
    deploymentLogSnapshotsRef.current = deploymentLogSnapshots;
  }, [deploymentLogSnapshots]);



  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && showFileEditor) {
        e.preventDefault();
        setFileSearch(prev => ({ ...prev, visible: true }));
        setTimeout(() => {
          const input = document.getElementById('file-editor-search-input');
          if (input) input.focus();
        }, 50);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFileEditor]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('no-scroll-deploy-embedded');
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      const data = await api.getStatus();
      const currentApps = data.apps || {};
      setApps(currentApps);
      if (data.profile) setAdminProfile(data.profile);


      setDevelopers(data.developers || []);
      setTeamMembers(data.teamMembers || []);
      setActiveDeployments(
        (data.activeDeployments || []).filter(d => !dismissedDeploymentIds.current.has(d.id))
      );

      // Poll logs for expanded app cards (near real-time monitoring)
      const expandedPaths = Object.keys(expandedLogs).filter(p => expandedLogs[p]);
      if (expandedPaths.length > 0) {
        expandedPaths.forEach(async (path) => {
          const appInfo = currentApps[path];
          if (!appInfo || (appInfo.type !== 'docker' && appInfo.type !== 'compose')) return;

          try {
            const owner = appInfo.owner || (appInfo.metadata && appInfo.metadata.owner) || (user && user.username);
            const appName = appInfo.appName || path.split('/').pop();
            const res = await api.getAppLogs(appName, { lines: 300, owner });
            setDeploymentRuntimeLogs(prev => ({
              ...prev,
              [path]: { logs: res.logs || [], fetchedAt: Date.now() }
            }));
          } catch (e) {
            console.warn(`Failed to fetch logs for ${path}:`, e.message);
          }
        });
      }
    } catch (error) {
      // console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [expandedLogs, user]);



  const executeSearch = () => {
    const query = fileSearch.query;
    if (!query) {
      setFileSearch(prev => ({ ...prev, matches: [], currentIndex: -1 }));
      return;
    }
    const content = editingFileData.content;
    const matches = [];
    const lowerQuery = query.toLowerCase();
    const lowerContent = content.toLowerCase();
    let i = lowerContent.indexOf(lowerQuery);
    while (i !== -1) {
      matches.push(i);
      i = lowerContent.indexOf(lowerQuery, i + query.length);
    }
    setFileSearch(prev => ({ ...prev, matches, currentIndex: matches.length > 0 ? 0 : -1 }));
    if (matches.length > 0 && textareaRef.current) {
      textareaRef.current.setSelectionRange(matches[0], matches[0] + query.length);
      textareaRef.current.focus();

      // Calculate scroll position dynamically based on line height
      const lines = content.substring(0, matches[0]).split('\n').length;
      const lineHeight = 21.6; // approx 1.5 * 0.9rem
      textareaRef.current.scrollTop = (lines * lineHeight) - (textareaRef.current.clientHeight / 2);
    }
  };

  const traverseFind = (direction) => {
    if (fileSearch.matches.length === 0) {
      if (fileSearch.query) executeSearch();
      return;
    }
    let nextIndex = fileSearch.currentIndex + direction;
    if (nextIndex < 0) nextIndex = fileSearch.matches.length - 1;
    if (nextIndex >= fileSearch.matches.length) nextIndex = 0;

    setFileSearch(prev => ({ ...prev, currentIndex: nextIndex }));

    if (textareaRef.current) {
      const start = fileSearch.matches[nextIndex];
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(start, start + fileSearch.query.length);

      const lines = editingFileData.content.substring(0, start).split('\n').length;
      const lineHeight = 21.6;
      textareaRef.current.scrollTop = (lines * lineHeight) - (textareaRef.current.clientHeight / 2);
    }
  };


  const toggleLogs = async (path) => {
    const isExpanding = !expandedLogs[path];
    setExpandedLogs(prev => ({ ...prev, [path]: isExpanding }));

    if (isExpanding) {
      const appInfo = apps[path];
      if (!appInfo || (appInfo.type !== 'docker' && appInfo.type !== 'compose' && !appInfo.metadata?.isSSR && appInfo.metadata?.servedBy !== 'docker' && appInfo.metadata?.servedBy !== 'pm2')) return;

      setDeploymentRuntimeLoading(prev => ({ ...prev, [path]: true }));
      try {
        const owner = appInfo.owner || (appInfo.metadata && appInfo.metadata.owner) || (user && user.username);
        const appName = appInfo.appName || path.split('/').pop();
        const res = await api.getAppLogs(appName, { lines: 300, owner, type: 'runtime' });
        setDeploymentRuntimeLogs(prev => ({
          ...prev,
          [path]: { logs: res.logs || [], fetchedAt: Date.now() }
        }));
      } catch (e) {
        console.warn(`Failed to fetch logs for ${path}:`, e.message);
      } finally {
        setDeploymentRuntimeLoading(prev => ({ ...prev, [path]: false }));
      }
    }
  };

  const loadFileBrowser = async (appName, owner, dir = '') => {
    setFileBrowser(prev => ({ ...prev, loading: true }));
    try {
      const response = await api.listContainerFiles(appName, dir, owner);
      if (response.isRestarting) {
        setFileBrowser(prev => ({ ...prev, loading: false }));
        return;
      }
      setFileBrowser(prev => {
        const newDirCache = { ...prev.dirCache, [dir || '']: response.entries || [] };
        return {
          ...prev,
          entries: response.entries || [],
          currentDir: dir || '',
          loading: false,
          isContainerOffline: response.isContainerOffline || false,
          dirCache: newDirCache,
          expandedDirs: { ...prev.expandedDirs, [dir || '']: true }
        };
      });
    } catch (err) {
      if (err.message && err.message.includes('restarting')) {
        // Container is restarting, just ignore quietly so we don't spam console
        setFileBrowser(prev => ({ ...prev, loading: false }));
        return;
      }
      console.error('Failed to list files:', err);
      setFileBrowser(prev => ({ ...prev, loading: false }));
    }
  };

  const shouldPreloadDir = (dirPath) => {
    const normalized = String(dirPath || '').trim().toLowerCase();
    if (!normalized) return true;
    const segments = normalized.split('/').filter(Boolean);
    return !segments.some((segment) => segment === '.git' || segment === 'node_modules');
  };

  const preloadFullFileTree = async (appName, owner) => {
    setFileBrowser(prev => ({ ...prev, loading: true }));
    const queue = [''];
    const visited = new Set();
    const dirCache = {};
    const expandedDirs = {};
    const maxDirs = 1500;
    const maxEntries = 8000;
    let entryCount = 0;

    try {
      while (queue.length > 0 && visited.size < maxDirs && entryCount < maxEntries) {
        const dir = queue.shift() || '';
        if (visited.has(dir)) continue;
        visited.add(dir);

        let response;
        try {
          response = await api.listContainerFiles(appName, dir, owner);
        } catch (_) {
          continue;
        }

        const entries = response?.entries || [];
        dirCache[dir] = entries;
        expandedDirs[dir] = true;
        entryCount += entries.length;

        entries.forEach((entry) => {
          if (entry?.isDir && shouldPreloadDir(entry.path)) {
            queue.push(entry.path);
          }
        });
      }

      setFileBrowser({
        entries: dirCache[''] || [],
        loading: false,
        currentDir: '',
        expandedDirs,
        dirCache
      });
    } catch (err) {
      console.error('Failed to preload file tree:', err);
      setFileBrowser(prev => ({ ...prev, loading: false }));
      await loadFileBrowser(appName, owner, '');
    }
  };

  const toggleDirExpansion = async (dirPath, appName, owner) => {
    setFileBrowser(prev => {
      const isExpanded = prev.expandedDirs[dirPath];
      if (isExpanded) {
        // Collapse
        return { ...prev, expandedDirs: { ...prev.expandedDirs, [dirPath]: false } };
      }
      return prev;
    });

    const isExpanded = fileBrowser.expandedDirs[dirPath];
    if (!isExpanded) {
      // Need to load this directory
      if (!fileBrowser.dirCache[dirPath]) {
        try {
          const response = await api.listContainerFiles(appName, dirPath, owner);
          setFileBrowser(prev => ({
            ...prev,
            dirCache: { ...prev.dirCache, [dirPath]: response.entries || [] },
            expandedDirs: { ...prev.expandedDirs, [dirPath]: true }
          }));
        } catch (err) {
          console.error('Failed to load directory:', err);
        }
      } else {
        setFileBrowser(prev => ({
          ...prev,
          expandedDirs: { ...prev.expandedDirs, [dirPath]: true }
        }));
      }
    }
  };

  const fetchFileContent = async (appName, owner, filePath) => {
    setEditingFileData(prev => ({ ...prev, loading: true, filename: filePath, appName, owner, error: null, content: '' }));
    setFileSearch(prev => ({ ...prev, visible: false, query: '', matches: [], currentIndex: -1 }));
    try {
      const response = await api.getFileContent(appName, filePath, owner);
      setEditingFileData(prev => ({ ...prev, content: response.content, originalContent: response.content, loading: false, error: null }));
    } catch (err) {
      console.error('Failed to fetch file:', err);
      const errorMsg = err.message || 'File not available';
      setEditingFileData(prev => ({ ...prev, loading: false, content: '', error: errorMsg }));
    }
  };

  const handleSaveFile = async (restart = false) => {
    onConfirm(
      'Save Changes',
      `Are you sure you want to save changes to ${editingFileData.filename}${restart ? ' and restart the container' : ''}?`,
      async () => {
        setEditingFileData(prev => ({ ...prev, saving: true }));
        try {
          await api.updateFileContent(
            editingFileData.appName,
            editingFileData.filename,
            editingFileData.content,
            editingFileData.owner,
            restart
          );
          setEditingFileData(prev => ({ ...prev, originalContent: editingFileData.content, saving: false }));
          onStatusMessage(`File saved ${restart ? 'and container restarted' : ''}`, 'success');
        } catch (err) {
          console.error('Failed to save file:', err);
          onStatusMessage(`Failed to save: ${err.message}`, 'error');
          setEditingFileData(prev => ({ ...prev, saving: false }));
        }
      },
      'info'
    );
  };

  const handleFileAiAssist = async () => {
    if (!fileAiState.prompt.trim() || !editingFileData.filename || fileAiState.loading) return;
    
    const currentPrompt = fileAiState.prompt;
    const history = fileAiState.messages;
    setFileAiState(prev => ({ 
      ...prev, 
      loading: true, 
      prompt: '',
      messages: [...prev.messages, { role: 'user', content: currentPrompt }]
    }));
    onStatusMessage('AI is modifying the file, please wait...', 'info');
    
    try {
      const res = await api.fileAiAssist(currentPrompt, editingFileData.filename, editingFileData.content, history);
      if (res.updatedContent) {
        setEditingFileData(prev => ({ ...prev, content: res.updatedContent }));
        onStatusMessage('File updated by AI. Please review and save.', 'success');
        setFileAiState(prev => ({
          ...prev,
          messages: [...prev.messages, { role: 'assistant', content: "I've updated the file. Please review the changes in the editor." }]
        }));
      } else {
        setFileAiState(prev => ({
          ...prev,
          messages: [...prev.messages, { role: 'assistant', content: "I couldn't modify the file. " + (res.message || "") }]
        }));
      }
    } catch (err) {
      console.error('AI Assist failed:', err);
      onStatusMessage('AI Assist failed: ' + err.message, 'error');
      setFileAiState(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'error', content: err.message }]
      }));
    } finally {
      setFileAiState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteFile = async (e, filePath, isDir = false) => {
    if (e) e.stopPropagation();
    onConfirm(
      'Delete ' + (isDir ? 'Directory' : 'File'),
      `Are you sure you want to delete ${isDir ? 'directory' : 'file'}: ${filePath}? This action cannot be undone.`,
      async () => {
        const restart = window.confirm(`${isDir ? 'Directory' : 'File'} deleted. Do you want to restart the container now so changes take effect?`);
        try {
          await api.deleteContainerFile(editingFileData.appName, filePath, editingFileData.owner, restart);
          if (editingFileData.filename === filePath) {
            setEditingFileData(prev => ({ ...prev, filename: '', content: '', originalContent: '' }));
          }
          const parentDir = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
          setFileBrowser(prev => {
            const newDirCache = { ...prev.dirCache };
            delete newDirCache[parentDir];
            return { ...prev, dirCache: newDirCache };
          });
          await loadFileBrowser(editingFileData.appName, editingFileData.owner, parentDir);
          onStatusMessage(`${isDir ? 'Directory' : 'File'} deleted ${restart ? 'and container restarted' : ''}`, 'success');
        } catch (err) {
          onStatusMessage('Failed to delete: ' + err.message, 'error');
        }
      },
      'danger'
    );
  };







  const handleCreateFileSubmit = async (e, basePath) => {
    e.preventDefault();
    if (!newFileName.trim()) {
      setCreatingFileIn(null);
      return;
    }

    // Normalize paths
    let fullPath = basePath ? `${basePath}/${newFileName}` : newFileName;
    fullPath = fullPath.replace(/\/+/g, '/'); // remove double slashes

    try {
      await api.updateFileContent(
        editingFileData.appName,
        fullPath,
        "",
        editingFileData.owner,
        false
      );

      setCreatingFileIn(null);
      setNewFileName('');

      // Refresh tree
      setFileBrowser(prev => ({ ...prev, dirCache: {}, expandedDirs: {} }));
      await loadFileBrowser(editingFileData.appName, editingFileData.owner, '');

      // Open the new file
      fetchFileContent(editingFileData.appName, editingFileData.owner, fullPath);
    } catch (err) {
      console.error('Failed to create file:', err);
      alert(`Failed to create file: ${err.message}`);
      setCreatingFileIn(null);
    }
  };

  const handleCreateFile = async (e, defaultDir) => {
    if (e) e.stopPropagation();

    let basePath = '';
    if (typeof defaultDir === 'string') {
      basePath = defaultDir;
    } else if (editingFileData.filename) {
      const parts = editingFileData.filename.split('/');
      parts.pop(); // remove file name
      if (parts.length > 0) {
        basePath = parts.join('/');
      }
    }

    if (basePath && !fileBrowser.expandedDirs[basePath]) {
      toggleDirExpansion(basePath, editingFileData.appName, editingFileData.owner);
    }

    setCreatingFileIn(basePath);
    setNewFileName('');
  };

  const renderNewFileInput = (basePath, depth) => {
    if (creatingFileIn !== basePath) return null;
    return (
      <div
        key={`new-input-${basePath}`}
        className="file-tree-item"
        style={{ paddingLeft: `${12 + depth * 16}px`, display: 'flex', alignItems: 'center' }}
      >
        <File size={14} className="file-tree-icon" style={{ opacity: 0.5, marginRight: '6px' }} />
        <form onSubmit={(e) => handleCreateFileSubmit(e, basePath)} style={{ flexGrow: 1, margin: 0, padding: 0 }}>
          <input
            autoFocus
            type="text"
            className="file-tree-new-input"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onBlur={() => setCreatingFileIn(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setCreatingFileIn(null);
                setNewFileName('');
              }
            }}
            placeholder="Filename.ext"
            style={{
              width: '100%',
              background: 'var(--surface)',
              border: '1px solid var(--primary)',
              borderRadius: '4px',
              color: 'var(--text-main)',
              padding: '2px 6px',
              fontSize: '0.85rem',
              outline: 'none',
              boxShadow: '0 0 0 2px rgba(var(--primary-rgb), 0.2)'
            }}
          />
        </form>
      </div>
    );
  };


  const renderFileTree = (dirPath, depth = 0) => {
    const entries = fileBrowser.dirCache[dirPath];
    if (!entries) return null;

    return (
      <React.Fragment>
        {renderNewFileInput(dirPath, depth)}
        {entries.map(entry => {
          const isSelected = editingFileData.filename === entry.path;
          if (entry.isDir) {
            const isExpanded = fileBrowser.expandedDirs[entry.path];
            return (
              <div key={entry.path}>
                <div
                  className={`file-tree-item file-tree-dir ${isExpanded ? 'expanded' : ''}`}
                  style={{ paddingLeft: `${12 + depth * 16}px` }}
                  onClick={() => toggleDirExpansion(entry.path, editingFileData.appName, editingFileData.owner)}
                >
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  {isExpanded ? <FolderOpen size={14} className="file-tree-icon folder" /> : <Folder size={14} className="file-tree-icon folder" />}
                  <span className="file-tree-name" style={{ flexGrow: 1 }}>{entry.name}</span>
                  <Plus
                    size={14}
                    className="file-tree-add-btn"
                    onClick={(e) => handleCreateFile(e, entry.path)}
                    title="Create file in this folder"
                    style={{ color: 'var(--text-secondary)', opacity: 0.7, cursor: 'pointer', flexShrink: 0 }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
                  />
                </div>
                {isExpanded && renderFileTree(entry.path, depth + 1)}
              </div>
            );
          }
          const ext = entry.name.split('.').pop().toLowerCase();
          const isBinary = ['png', 'jpg', 'jpeg', 'gif', 'ico', 'svg', 'woff', 'woff2', 'ttf', 'eot', 'mp3', 'mp4', 'zip', 'gz', 'tar', 'pdf', 'exe', 'dll', 'so', 'o'].includes(ext);
          return (
            <div
              key={entry.path}
              className={`file-tree-item file-tree-file ${isSelected ? 'active' : ''} ${isBinary ? 'binary' : ''}`}
              style={{ paddingLeft: `${12 + depth * 16}px` }}
              onClick={() => !isBinary && fetchFileContent(editingFileData.appName, editingFileData.owner, entry.path)}
              title={isBinary ? 'Binary file - cannot edit' : entry.path}
            >
              <File size={14} className={`file-tree-icon ${isBinary ? 'binary' : ''}`} />
              <span className="file-tree-name" style={{ flexGrow: 1 }}>{entry.name}</span>
              <Trash2
                size={14}
                className="file-tree-delete-btn"
                onClick={(e) => handleDeleteFile(e, entry.path)}
                title="Delete file"
                style={{ color: 'var(--danger)', opacity: 0.7, cursor: 'pointer', flexShrink: 0 }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
              />
            </div>
          );
        })}
      </React.Fragment>
    );
  };


  useEffect(() => {
    const handleOpenFileEditor = (e) => {
      const { appName, owner, preloadAllFiles } = e.detail;
      setEditingFileData({ appName, owner, filename: '', content: '', originalContent: '', loading: false, saving: false, error: null });
      setFileBrowser({ entries: [], loading: false, currentDir: '', expandedDirs: {}, dirCache: {} });
      setShowFileEditor(true);
      setTimeout(() => {
        if (preloadAllFiles) {
          preloadFullFileTree(appName, owner);
        } else {
          loadFileBrowser(appName, owner, '');
        }
      }, 50);
    };
    window.addEventListener('open-file-editor', handleOpenFileEditor);
    return () => window.removeEventListener('open-file-editor', handleOpenFileEditor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    const handleImmediateRefresh = () => loadData();
    window.addEventListener('switch-to-dashboard', handleImmediateRefresh);
    window.addEventListener('refresh-dashboard', handleImmediateRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('switch-to-dashboard', handleImmediateRefresh);
      window.removeEventListener('refresh-dashboard', handleImmediateRefresh);
    };
  }, [loadData]);

  useEffect(() => {
    if (!activeDeployments || activeDeployments.length === 0) return;

    setDeploymentLogSnapshots((prev) => {
      const next = { ...prev };

      for (const dep of activeDeployments) {
        const depAppName = dep.meta?.appName;
        const depOwner = dep.meta?.owner;
        if (!depAppName || !depOwner) continue;

        const match = Object.entries(apps).find(([path, appInfo]) => {
          const parts = path.split('/');
          return parts.length >= 3 && parts[1] === depOwner && (appInfo.appName === depAppName || parts[2] === depAppName);
        });

        if (!match) continue;

        const [path] = match;
        const existing = next[path];
        const currentLogs = Array.isArray(dep.logs) ? dep.logs : [];

        if (!existing || existing.deploymentId !== dep.id || (existing.logs || []).length < currentLogs.length) {
          next[path] = {
            deploymentId: dep.id,
            status: dep.status,
            logs: currentLogs,
            source: 'active-session',
            error: dep.error?.message || null
          };
        }
      }

      return next;
    });
  }, [activeDeployments, apps]);

  useEffect(() => {
    let cancelled = false;

    const loadMissingDeploymentLogs = async () => {
      const entries = Object.entries(apps).filter(([path, appInfo]) => appInfo?.type !== 'zip' && appInfo?.metadata?.deploymentId);

      if (entries.length === 0) return;

      await Promise.all(entries.map(async ([path, appInfo]) => {
        const deploymentId = appInfo.metadata.deploymentId;
        const storedLogs = Array.isArray(appInfo.metadata.deploymentLogs) ? appInfo.metadata.deploymentLogs : [];
        const cached = deploymentLogSnapshotsRef.current[path];

        if (cached && cached.deploymentId === deploymentId) {
          return;
        }

        if (storedLogs.length > 0) {
          if (!cancelled) {
            setDeploymentLogSnapshots(prev => ({
              ...prev,
              [path]: {
                deploymentId,
                status: appInfo.metadata.deploymentStatus || 'completed',
                logs: storedLogs,
                source: 'metadata',
                error: null
              }
            }));
          }
          return;
        }

        try {
          const session = await api.getDeploymentStatus(deploymentId);
          if (cancelled) return;
          setDeploymentLogSnapshots(prev => ({
            ...prev,
            [path]: {
              deploymentId,
              status: session.status,
              logs: session.logs || [],
              source: 'session',
              error: null
            }
          }));
        } catch (err) {
          if (!cancelled) {
            setDeploymentLogSnapshots(prev => ({
              ...prev,
              [path]: {
                deploymentId,
                status: appInfo.metadata.deploymentStatus || 'unknown',
                logs: [],
                source: 'error',
                error: err.message
              }
            }));
          }
        }
      }));
    };

    loadMissingDeploymentLogs();

    return () => {
      cancelled = true;
    };
  }, [apps]);

  const handleDismissDeployment = async (depId) => {
    dismissedDeploymentIds.current.add(depId);
    setActiveDeployments(prev => prev.filter(d => d.id !== depId));
    try {
      await api.dismissDeployment(depId);
    } catch (err) {
      console.warn('Failed to dismiss deployment session:', err);
      dismissedDeploymentIds.current.delete(depId);
      try {
        const data = await api.getStatus();
        setActiveDeployments(
          (data.activeDeployments || []).filter(d => !dismissedDeploymentIds.current.has(d.id))
        );
      } catch (_) {}
    }
  };

  /*
  const handleCancelDeployment = (depId) => {
    onConfirm(
      'Cancel Deployment',
      'Are you sure you want to cancel this deployment? Any partially created resources will be cleaned up.',
      async () => {
        try {
          await api.cancelDeployment(depId);
          loadData();
        } catch (err) {
          console.warn('Failed to cancel deployment:', err);
          onStatusMessage('Failed to cancel: ' + err.message, 'error');
        }
      },
      'warning'
    );
  };
  */

  const handleDeleteApp = async (appPath, appName) => {
    onConfirm(
      'Delete Application',
      `Are you sure you want to delete ${appName}? This will remove all container data and configuration permanently.`,
      async () => {
        setActionLoading(prev => ({ ...prev, [appPath]: 'deleting' }));
        try {
          await api.deleteApp(appPath);
          onStatusMessage('App deleted successfully', 'success');
          loadData();
        } catch (err) {
          onStatusMessage('Failed to delete app: ' + err.message, 'error');
        } finally {
          setActionLoading(prev => ({ ...prev, [appPath]: null }));
        }
      },
      'danger'
    );
  };



  const handleCopyLink = (url, path) => {
    copyToClipboard(url).then(() => {
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch(err => {
      console.error('Copy failed', err);
      onStatusMessage && onStatusMessage('Copy failed', 'error');
    });
  };


  const saveAppConfig = async (e) => {
    e.preventDefault();
    if (!editingAppConfig) return;
    const { path, appName, owner, meta } = editingAppConfig;
    
    // HCI Improvement: Port Validation
    if (meta.internalPort && (isNaN(meta.internalPort) || meta.internalPort < 1 || meta.internalPort > 65535)) {
      onStatusMessage('Invalid internal port. Must be between 1 and 65535.', 'error');
      return;
    }
    
    onConfirm(
      'Save Settings',
      'Are you sure you want to update these settings?' + (meta.type === 'docker' ? ' The container will be automatically restarted to apply changes.' : ''),
      async () => {
        setActionLoading(prev => ({ ...prev, [path]: 'saving' }));
        try {
          const res = await api.updateAppConfig(appName, meta, owner);
          if (res.warning) {
            onStatusMessage(res.warning, 'warning');
          } else {
            onStatusMessage(res.rebuilt ? 'Settings updated and container restarted successfully.' : 'Settings updated successfully.', 'success');
          }
          setEditingAppConfig(null);
          await loadData();
        } catch (err) {
          console.error('Failed to update config:', err);
          onStatusMessage('Failed to update settings: ' + err.message, 'error');
        } finally {
          setActionLoading(prev => ({ ...prev, [path]: null }));
        }
      },
      'info'
    );
  };

  const saveDomain = async (path, appName, owner) => {
    const domain = (domainEdits[path] || '').trim();

    // HCI Improvement: Error Prevention (Domain Validation)
    if (domain) {
      const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
      if (!domainRegex.test(domain)) {
        onStatusMessage('Invalid domain format. Please use a valid domain name.', 'error');
        return;
      }
    }

    onConfirm(
      'Update Domain',
      `Are you sure you want to change the domain for ${appName} to ${domain || 'default'}?`,
      async () => {
        setDomainSaving(prev => ({ ...prev, [path]: true }));
        try {
          await api.updateAppDomain(appName, domain || '', user.role === 'admin' ? owner : undefined);
          onStatusMessage('Domain updated', 'success');
          await loadData();
          setEditingDomain(prev => ({ ...prev, [path]: false }));
        } catch (e) {
          onStatusMessage('Failed to update domain: ' + e.message, 'error');
        } finally {
          setDomainSaving(prev => ({ ...prev, [path]: false }));
        }
      },
      'info'
    );
  };

  const handleContainerAction = async (appPath, appName, action, owner) => {
    onConfirm(
      action.charAt(0).toUpperCase() + action.slice(1) + ' Container',
      `Are you sure you want to ${action} ${appName}?`,
      async () => {
        const loadingKey = appPath || appName;
        setActionLoading(prev => ({ ...prev, [loadingKey]: action }));

        setApps(prev => {
          if (!prev[appPath]) return prev;
          return {
            ...prev,
            [appPath]: { ...prev[appPath], status: action === 'start' ? 'running' : 'stopped' }
          };
        });

        try {
          await api.controlContainer(appName, action, user && user.role === 'admin' && owner ? { owner } : {});
          onStatusMessage(`Container ${action}ed successfully`, 'success');
          await loadData();
        } catch (err) {
          onStatusMessage(`Failed to ${action} container: ` + err.message, 'error');
          await loadData();
        } finally {
          setActionLoading(prev => ({ ...prev, [loadingKey]: null }));
        }
      },
      'info'
    );
  };

  const handleCreateSnapshot = async (appPath, appName, owner) => {
    onConfirm(
      'Create Snapshot',
      `Are you sure you want to snapshot ${appName}? This captures any manual terminal changes outside of /app. The snapshot will be used during the next rebuild.`,
      async () => {
        const loadingKey = appPath || appName;
        setActionLoading(prev => ({ ...prev, [loadingKey]: 'snapshot' }));
        try {
          const result = await api.post(`/apps/${appName}/snapshot`);
          if (result && result.success) {
            onStatusMessage(`Snapshot ${result.snapshot?.image} created successfully.`, 'success');
            loadData();
          }
        } catch (err) {
          onStatusMessage(err.response?.data?.error || err.message || 'Failed to create snapshot', 'error');
        } finally {
          setActionLoading(prev => {
            const next = { ...prev };
            delete next[loadingKey];
            return next;
          });
        }
      },
      'info'
    );
  };

  const handleDownloadSnapshot = async (appPath, appName, owner) => {
    const loadingKey = appPath || appName;
    setActionLoading(prev => ({ ...prev, [loadingKey]: 'download_snapshot' }));
    try {
      await api.downloadAppSnapshot(appName, owner);
      onStatusMessage('Snapshot downloaded successfully.', 'success');
    } catch (err) {
      onStatusMessage(err.response?.data?.error || err.message || 'Failed to download snapshot', 'error');
    } finally {
      setActionLoading(prev => {
        const next = { ...prev };
        delete next[loadingKey];
        return next;
      });
    }
  };

  const handleRebuild = async (appPath, appName, owner) => {
    onConfirm(
      'Rebuild Application',
      `Are you sure you want to rebuild ${appName}? This will re-install dependencies and run the build command.`,
      async () => {
        const loadingKey = appPath || appName;
        setActionLoading(prev => ({ ...prev, [loadingKey]: 'rebuild' }));
        try {
          // Generate a deployment ID so the rebuild session is trackable
          const deploymentId = window.crypto?.randomUUID?.()
            || `rebuild-${appName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

          const result = await api.rebuildFrontend(
            appName,
            user && user.role === 'admin' && owner ? owner : undefined,
            { deploymentId }
          );

          if (result && result.status === 'running') {
            onStatusMessage(
              `Rebuild started for ${appName}. Watch the Active Deployments panel for progress.`,
              'info'
            );
          } else {
            onStatusMessage(`Application ${appName} rebuilt successfully.`, 'success');
          }
          await loadData();
        } catch (err) {
          onStatusMessage(`Failed to rebuild: ` + err.message, 'error');
        } finally {
          setActionLoading(prev => ({ ...prev, [loadingKey]: null }));
        }
      },
      'info'
    );
  };


  // Compute scoped apps & aggregates: admin (global), admin selected developer, or developer user only
  const scopedApps = (() => {
    if (user && (user.role === 'admin' || user.isOwner)) {
      if (memberFilter === 'mine') {
        return Object.values(apps).filter(a => a.owner === user.username || (a.metadata && a.metadata.owner === user.username));
      }
      if (memberFilter && memberFilter !== 'all') {
        const target = memberFilter.replace(/^@/, '');
        return Object.values(apps).filter(a => a.owner === target || (a.metadata && a.metadata.owner === target));
      }
      if (selectedDeveloper && selectedDeveloper !== 'all') {
        return Object.values(apps).filter(a => a.owner === selectedDeveloper || (a.metadata && a.metadata.owner === selectedDeveloper));
      }
      return Object.values(apps);
    }
    if (user) return Object.values(apps).filter(a => a.owner === user.username);
    return Object.values(apps);
  })();



  const scopedStaticCount = scopedApps.filter(a => a.type !== 'docker' && a.type !== 'compose').length;
  const scopedDockerCount = scopedApps.filter(a => a.type === 'docker' || a.type === 'compose').length;

  const totalStaticCount = Object.values(apps).filter(a => a.type !== 'docker' && a.type !== 'compose').length;
  const totalDockerCount = Object.values(apps).filter(a => a.type === 'docker' || a.type === 'compose').length;
  const totalDockerRunningCount = Object.values(apps).filter(a => (a.type === 'docker' || a.type === 'compose') && (a.status === 'running' || !!a.port)).length;
  const scopedDockerRunningCount = scopedApps.filter(a => (a.type === 'docker' || a.type === 'compose') && (a.status === 'running' || !!a.port)).length;
  const isGlobalAdminView = false;

  if (loading) {
    return (
      <div className="dashboard-loading-screen dash-loader" role="status" aria-live="polite" aria-label="Loading dashboard">
        <div className="dash-loader-orb" aria-hidden="true">
          <div className="dash-loader-glow" />
          <div className="dash-loader-ring" />
          <div className="dash-loader-ring dash-loader-ring--inner" />
          <div className="dash-loader-orbit">
            <span className="dash-loader-dot" />
          </div>
          <div className="dash-loader-core" />
        </div>
        <div className="dash-loader-copy">
          <h2 className="dash-loader-title">Loading Dashboard</h2>
          <p className="dash-loader-sub">Fetching your applications and data...</p>
          <div className="dash-loader-bars" aria-hidden="true">
            <span /><span /><span /><span />
          </div>
        </div>
      </div>
    );
  }

  const rawAppEntries = Object.entries(apps).filter(([path, appInfo]) => {
    if (/_extra\d+$/.test(path)) return false;
    const matchesSearch = !searchTerm || (appInfo.appName || path).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || (filterType === 'zip' && appInfo.type !== 'docker' && appInfo.type !== 'compose') || (filterType === 'docker' && (appInfo.type === 'docker' || appInfo.type === 'compose'));
    const effectiveOwner = appInfo.owner || (appInfo.metadata && appInfo.metadata.owner) || (path.startsWith('/') ? path.split('/')[1] : path.split('/')[0]);
    let matchesMember = true;
    if (user && (user.role === 'admin' || user.isOwner)) {
      if (memberFilter === 'mine') {
        matchesMember = effectiveOwner === user.username;
      } else if (memberFilter && memberFilter !== 'all') {
        const target = memberFilter.replace(/^@/, '');
        matchesMember = effectiveOwner === target;
      } else if (selectedDeveloper && selectedDeveloper !== 'all') {
        matchesMember = effectiveOwner === selectedDeveloper;
      }
    } else if (user) {
      matchesMember = effectiveOwner === user.username;
    }
    return matchesSearch && matchesFilter && matchesMember;
  });

  const groupedApps = {};
  for (const [path, appInfo] of rawAppEntries) {
    const key = appInfo.appName ? `${appInfo.owner}_${appInfo.appName}` : path;
    if (!groupedApps[key]) {
      groupedApps[key] = [];
    }
    groupedApps[key].push({ path, appInfo });
  }

  const appEntries = Object.values(groupedApps).map(group => {
    // Sort to find the primary app (docker/compose > running > static)
    group.sort((a, b) => {
      const typeScore = (type) => type === 'docker' || type === 'compose' ? 3 : (type === 'ssr' ? 2 : 1);
      const statusScore = (status) => status === 'running' ? 2 : 1;
      const scoreA = typeScore(a.appInfo.type) * 10 + statusScore(a.appInfo.status);
      const scoreB = typeScore(b.appInfo.type) * 10 + statusScore(b.appInfo.status);
      return scoreB - scoreA;
    });

    const primary = group[0];
    const storedEntryPoints = Array.isArray(primary.appInfo.metadata?.entryPoints)
      ? primary.appInfo.metadata.entryPoints
      : (Array.isArray(primary.appInfo.entryPoints) ? primary.appInfo.entryPoints : []);
    // Always merge lastBindings so Admin/User dashboards keep accurate roles/labels.
    const runtimeBindings = Array.isArray(primary.appInfo.metadata?.lastBindings)
      ? primary.appInfo.metadata.lastBindings.map(binding => ({
        ...binding,
        role: binding.role || binding.type || 'service',
        label: binding.label || null,
        path: binding.path || binding.dir,
        domain: binding.domain || ''
      }))
      : [];
    const groupedEntryPoints = group.slice(1).map(g => ({ path: g.path, ...g.appInfo }));
    const primaryDomain = primary.appInfo.domain;
    const additional = [...groupedEntryPoints, ...storedEntryPoints, ...runtimeBindings]
      .filter(entry => entry && (entry.domain || entry.port || entry.path))
      .filter((entry, index, entries) => {
        // The primary service can also be present in metadata.entryPoints.
        if (entry.domain && entry.domain === primaryDomain) return false;
        const identity = entry.domain || `${entry.path || ''}:${entry.port || ''}`;
        return entries.findIndex(candidate => (candidate.domain || `${candidate.path || ''}:${candidate.port || ''}`) === identity) === index;
      })
      .map(entry => {
        if (entry.label) return entry;
        const pathHint = entry.path || entry.dir || entry.role || '';
        const leaf = String(pathHint).split('/').filter(Boolean).pop() || '';
        const role = String(entry.role || '').toLowerCase();
        let label = null;
        if (role === 'admin' || /admin/i.test(leaf)) label = 'Admin Dashboard';
        else if (role === 'user' || /user/i.test(leaf)) label = 'User Dashboard';
        else if (role === 'backend' || role === 'api' || /backend|server|api/i.test(leaf)) label = 'Backend API';
        else if (role === 'frontend' || /front|client|web/i.test(leaf)) label = 'Frontend';
        else if (leaf) label = leaf.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return label ? { ...entry, label } : entry;
      });
    return [primary.path, { ...primary.appInfo, ...(additional.length ? { additionalEntryPoints: additional } : {}) }];
  });





  return (
    <>
      <div className="dashboard-layout-wrapper" onClick={() => { if(activeIdeDropdown) setActiveIdeDropdown(null); }}>
      {(adminProfile?.subscriptionStatus === 'past_due' || adminProfile?.subscriptionStatus === 'unpaid' || adminProfile?.subscriptionStatus === 'failing') && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          fontWeight: '500'
        }}>
          <Activity size={24} />
          <div>
            <strong style={{ display: 'block', fontSize: '1.1rem', marginBottom: '0.2rem' }}>Action Required: Billing Issue Detected</strong>
            Your latest payment has failed or is past due. Please update your billing information immediately to avoid account suspension.
          </div>
        </div>
      )}

      {globalAnnouncement.active && (
        <div style={{
          background: 'linear-gradient(90deg, #dc2626 0%, #ef4444 40%, #b91c1c 100%)',
          border: '1px solid rgba(239, 68, 68, 0.6)',
          boxShadow: '0 4px 16px rgba(220, 38, 38, 0.3)',
          color: '#ffffff',
          padding: '0.6rem 1.25rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.45rem',
            background: 'rgba(0, 0, 0, 0.35)',
            padding: '0.35rem 0.85rem',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 800,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.15)'
          }}>
            <Megaphone size={16} color="#fca5a5" />
            <span>System Broadcast</span>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {/* eslint-disable-next-line jsx-a11y/no-distracting-elements */}
            <marquee behavior="scroll" direction="left" scrollamount="5" style={{ fontSize: '0.95rem', fontWeight: 600, color: '#ffffff', verticalAlign: 'middle', margin: 0 }}>
              {globalAnnouncement.message}
            </marquee>
          </div>
        </div>
      )}

      {activeTab === 'deployments' && user && user.role === 'admin' && selectedDeveloper !== null && (
        <div className="dashboard-header" style={{ marginBottom: '1rem' }}>
          <div className="dashboard-header-title-area">
            <button onClick={() => setSelectedDeveloper(null)} className="btn-ghost dashboard-back-btn"><ArrowLeft size={18} strokeWidth={2.5} /></button>
            <h2>{selectedDeveloper === 'all' ? 'All Deployments' : `Deployments for ${selectedDeveloper}`}</h2>
          </div>
        </div>
      )}





      {activeTab === 'stats' && user && (() => {
        const isAdmin = user.role === 'admin' || user.isOwner;

        if (isAdmin) {
          // ==================== ADMIN VIEW ====================
          const allOrgApps = Object.values(apps);
          const totalOrgAppsCount = allOrgApps.length;
          const totalOrgRunningCount = allOrgApps.filter(a => a.status?.toLowerCase().includes('running') || a.status?.toLowerCase().includes('up') || a.status === 'static').length;
          const totalOrgMemBytes = allOrgApps.reduce((acc, a) => acc + (a.resources?.memUsage || 0), 0);
          const totalOrgStorageBytes = allOrgApps.reduce((acc, a) => acc + (a.resources?.storageUsage || 0), 0);
          const totalOrgCpuPercent = allOrgApps.reduce((acc, a) => acc + (parseFloat(a.resources?.cpuPercent) || 0), 0);

          const planLimits = adminProfile?.limits || {};
          const planMaxDeployments = planLimits.maxDeployments || 5;
          const planMaxMemBytes = parseMemoryToBytes(planLimits.maxTotalMemory || '8192m');
          const planMaxStorageBytes = (planLimits.maxStorageGB || 20) * 1024 * 1024 * 1024;
          const planMaxCpu = planLimits.maxTotalCpu || 4.0;
          const planTierDisplay = (adminProfile?.tierName || adminProfile?.tier || user?.packageTier || 'Pro').toUpperCase();

          const remainingDeployments = Math.max(0, planMaxDeployments - totalOrgAppsCount);
          const remainingMemBytes = Math.max(0, planMaxMemBytes - totalOrgMemBytes);
          const remainingStorageBytes = Math.max(0, planMaxStorageBytes - totalOrgStorageBytes);

          // Build Breakdown Rows
          const breakdownRows = [];

          // 1. Admin Row (You)
          const adminApps = allOrgApps.filter(a => a.owner === user.username || (a.metadata && a.metadata.owner === user.username));
          const adminRunning = adminApps.filter(a => a.status?.toLowerCase().includes('running') || a.status?.toLowerCase().includes('up') || a.status === 'static').length;
          const adminMem = adminApps.reduce((acc, a) => acc + (a.resources?.memUsage || 0), 0);
          const adminStorage = adminApps.reduce((acc, a) => acc + (a.resources?.storageUsage || 0), 0);
          const adminCpu = adminApps.reduce((acc, a) => acc + (parseFloat(a.resources?.cpuPercent) || 0), 0);

          breakdownRows.push({
            username: user.username,
            displayName: `${user.fullName || user.username} (You)`,
            email: user.email || '',
            role: 'Admin',
            isAdmin: true,
            appCount: adminApps.length,
            runningCount: adminRunning,
            memBytes: adminMem,
            storageBytes: adminStorage,
            cpuPercent: adminCpu,
            hasCustomLimits: false,
            customLimits: null
          });

          // 2. Team Member / Developer Rows
          const seen = new Set([user.username]);
          (teamMembers || []).forEach(m => {
            if (!seen.has(m.username)) {
              seen.add(m.username);
              const mApps = allOrgApps.filter(a => a.owner === m.username || (a.metadata && a.metadata.owner === m.username));
              const mRunning = mApps.filter(a => a.status?.toLowerCase().includes('running') || a.status?.toLowerCase().includes('up') || a.status === 'static').length;
              const mMem = mApps.reduce((acc, a) => acc + (a.resources?.memUsage || 0), 0);
              const mStorage = mApps.reduce((acc, a) => acc + (a.resources?.storageUsage || 0), 0);
              const mCpu = mApps.reduce((acc, a) => acc + (parseFloat(a.resources?.cpuPercent) || 0), 0);
              const cl = m.customLimits || {};
              const hasCl = Boolean(cl.maxDeployments || cl.memoryLimitMB || cl.storageGB || cl.storageLimitGB || cl.cpuLimit);

              breakdownRows.push({
                username: m.username,
                displayName: m.fullName || m.username,
                email: m.email || '',
                role: m.role || 'Developer',
                isAdmin: false,
                appCount: mApps.length,
                runningCount: mRunning,
                memBytes: mMem,
                storageBytes: mStorage,
                cpuPercent: mCpu,
                hasCustomLimits: hasCl,
                customLimits: cl
              });
            }
          });

          // Fallback: If developers exist in `developers` list not yet in `teamMembers`
          (developers || []).forEach(devUsername => {
            if (!seen.has(devUsername)) {
              seen.add(devUsername);
              const mApps = allOrgApps.filter(a => a.owner === devUsername || (a.metadata && a.metadata.owner === devUsername));
              const mRunning = mApps.filter(a => a.status?.toLowerCase().includes('running') || a.status?.toLowerCase().includes('up') || a.status === 'static').length;
              const mMem = mApps.reduce((acc, a) => acc + (a.resources?.memUsage || 0), 0);
              const mStorage = mApps.reduce((acc, a) => acc + (a.resources?.storageUsage || 0), 0);
              const mCpu = mApps.reduce((acc, a) => acc + (parseFloat(a.resources?.cpuPercent) || 0), 0);

              breakdownRows.push({
                username: devUsername,
                displayName: devUsername,
                email: '',
                role: 'Developer',
                isAdmin: false,
                appCount: mApps.length,
                runningCount: mRunning,
                memBytes: mMem,
                storageBytes: mStorage,
                cpuPercent: mCpu,
                hasCustomLimits: false,
                customLimits: null
              });
            }
          });

          return (
            <div className="dashboard-stats-wrapper fade-in-up">
              {/* Header */}
              <div className="dashboard-stats-header" style={{ marginBottom: '1.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div className="dashboard-stats-header-info">
                  <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                    <Activity size={22} color="var(--primary)" />
                    Organization Resource Usage
                  </h2>
                  <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                    Aggregate resource consumption and member-by-member quota allocation breakdown
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '0.8rem', background: 'rgba(var(--primary-rgb), 0.12)', color: 'var(--primary)', padding: '0.35rem 0.85rem', borderRadius: '20px', fontWeight: 700, border: '1px solid rgba(var(--primary-rgb), 0.25)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    ● {planTierDisplay.endsWith('PLAN') ? planTierDisplay : `${planTierDisplay} PLAN`}
                  </span>
                  <button onClick={loadData} className="premium-refresh-btn" title="Refresh resource metrics" style={{ height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <RotateCw size={15} />
                  </button>
                </div>
              </div>

              {/* Top Summary Cards: Organization Total */}
              <div className="stats-section-group" style={{ marginBottom: '2rem' }}>
                <div className="section-group-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span className="group-icon" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', padding: '0.4rem', borderRadius: '8px', display: 'flex' }}>
                      <Server size={18} />
                    </span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>Organization Total Usage</h3>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Aggregated resource consumption across all active team members</span>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {totalOrgAppsCount} Total Apps Deployed
                  </span>
                </div>

                <div className="stats-grid premium-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
                  {/* Deployments Card */}
                  <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                        <Server size={16} color="#3b82f6" /> Deployments (Apps)
                      </span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6' }}>Plan Quota</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                      {totalOrgAppsCount} / {planMaxDeployments}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Active: {totalOrgAppsCount}</span>
                      <span style={{ color: remainingDeployments === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                        {remainingDeployments} Remaining
                      </span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, (totalOrgAppsCount / (planMaxDeployments || 1)) * 100)}%`,
                        height: '100%',
                        background: (totalOrgAppsCount / (planMaxDeployments || 1)) > 0.85 ? 'var(--danger)' : '#3b82f6',
                        borderRadius: '4px',
                        transition: 'width 0.4s ease'
                      }} />
                    </div>
                  </div>

                  {/* Memory (RAM) Card */}
                  <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                        <Database size={16} color="#a855f7" /> Memory (RAM)
                      </span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#a855f7' }}>Live Usage</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                      {formatBytesValue(totalOrgMemBytes)} / {formatBytesValue(planMaxMemBytes)}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Used: {formatBytesValue(totalOrgMemBytes)}</span>
                      <span style={{ color: remainingMemBytes === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                        {formatBytesValue(remainingMemBytes)} Remaining
                      </span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, (totalOrgMemBytes / (planMaxMemBytes || 1)) * 100)}%`,
                        height: '100%',
                        background: (totalOrgMemBytes / (planMaxMemBytes || 1)) > 0.85 ? 'var(--danger)' : '#a855f7',
                        borderRadius: '4px',
                        transition: 'width 0.4s ease'
                      }} />
                    </div>
                  </div>

                  {/* Storage (Disk) Card */}
                  <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                        <HardDrive size={16} color="#06b6d4" /> Disk Storage
                      </span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#06b6d4' }}>Persistent</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                      {formatBytesValue(totalOrgStorageBytes)} / {formatBytesValue(planMaxStorageBytes)}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Used: {formatBytesValue(totalOrgStorageBytes)}</span>
                      <span style={{ color: remainingStorageBytes === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                        {formatBytesValue(remainingStorageBytes)} Remaining
                      </span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, (totalOrgStorageBytes / (planMaxStorageBytes || 1)) * 100)}%`,
                        height: '100%',
                        background: (totalOrgStorageBytes / (planMaxStorageBytes || 1)) > 0.85 ? 'var(--danger)' : '#06b6d4',
                        borderRadius: '4px',
                        transition: 'width 0.4s ease'
                      }} />
                    </div>
                  </div>

                  {/* CPU Cores Card */}
                  <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                        <Activity size={16} color="#f59e0b" /> CPU Cores
                      </span>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f59e0b' }}>Utilization</span>
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                      {totalOrgCpuPercent.toFixed(1)}% / {planMaxCpu} Cores
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                      <span>Active Containers: {totalOrgRunningCount}</span>
                      <span style={{ color: 'var(--text-muted)' }}>Max {adminProfile?.limits?.maxCpuPerContainer || 0.5} CPU/app</span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.min(100, (totalOrgCpuPercent / ((planMaxCpu || 1) * 100)) * 100)}%`,
                        height: '100%',
                        background: (totalOrgCpuPercent / ((planMaxCpu || 1) * 100)) > 0.85 ? 'var(--danger)' : '#f59e0b',
                        borderRadius: '4px',
                        transition: 'width 0.4s ease'
                      }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Row: User-by-User Breakdown Table */}
              <div className="card" style={{ padding: '1.5rem', borderRadius: '14px', background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                    <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(var(--primary-rgb), 0.12)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Users size={19} />
                    </span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-main)' }}>User-by-User Resource Breakdown</h3>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Live application usage and allocated quota limits per team member</div>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                    {breakdownRows.length} Total Account(s)
                  </span>
                </div>

                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2, rgba(255,255,255,0.02))' }}>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>User</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Apps Active</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>RAM Used</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Storage Used</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>CPU Used</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Quota Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownRows.map((row) => (
                        <tr key={row.username} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s ease' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover, rgba(255,255,255,0.02))'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                          {/* User Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                              <div style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '10px',
                                background: row.isAdmin ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(16, 185, 129, 0.12)',
                                color: row.isAdmin ? 'var(--primary)' : '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                flexShrink: 0
                              }}>
                                {row.isAdmin ? <Shield size={18} /> : <User size={18} />}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                                  <span>{row.displayName}</span>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.15rem 0.5rem',
                                    borderRadius: '12px',
                                    background: row.isAdmin ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(16, 185, 129, 0.12)',
                                    color: row.isAdmin ? 'var(--primary)' : '#10b981',
                                    fontWeight: 700,
                                    textTransform: 'capitalize'
                                  }}>
                                    {row.role}
                                  </span>
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                                  {row.email ? row.email : `@${row.username}`}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Apps Active Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                              {row.appCount} {row.appCount === 1 ? 'app' : 'apps'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: row.runningCount > 0 ? '#10b981' : 'var(--text-muted)' }}>
                              {row.runningCount} running
                            </div>
                          </td>

                          {/* RAM Used Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                              {formatBytesValue(row.memBytes)}
                            </div>
                          </td>

                          {/* Storage Used Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                              {formatBytesValue(row.storageBytes)}
                            </div>
                          </td>

                          {/* CPU Used Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.92rem', color: 'var(--text-main)' }}>
                              {row.cpuPercent.toFixed(1)}%
                            </div>
                          </td>

                          {/* Quota Limit Cell */}
                          <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                            {row.isAdmin ? (
                              <span style={{ fontSize: '0.8rem', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', padding: '0.3rem 0.7rem', borderRadius: '6px', fontWeight: 600, border: '1px solid rgba(var(--primary-rgb), 0.2)' }}>
                                Plan Total
                              </span>
                            ) : row.hasCustomLimits ? (
                              <div style={{ fontSize: '0.82rem', color: 'var(--text-main)', background: 'rgba(255,255,255,0.03)', padding: '0.35rem 0.65rem', borderRadius: '6px', border: '1px solid var(--border)', display: 'inline-block' }}>
                                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Max: </span>
                                {[
                                  row.customLimits.maxDeployments ? `${row.customLimits.maxDeployments} apps` : null,
                                  row.customLimits.memoryLimitMB ? (row.customLimits.memoryLimitMB >= 1024 ? `${(row.customLimits.memoryLimitMB / 1024).toFixed(1)}GB RAM` : `${row.customLimits.memoryLimitMB}MB RAM`) : null,
                                  row.customLimits.storageGB ? `${row.customLimits.storageGB}GB Disk` : null,
                                  row.customLimits.cpuLimit ? `${row.customLimits.cpuLimit} CPU` : null,
                                ].filter(Boolean).join(', ')}
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)', padding: '0.3rem 0.7rem', borderRadius: '6px', fontWeight: 500 }}>
                                Shared Pool
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        }

        // ==================== DEVELOPER / TEAM MEMBER VIEW ====================
        const devCustomQuota = adminProfile?.customLimits || user?.customLimits || {};
        const hasDevCustomQuota = Boolean(devCustomQuota.maxDeployments || devCustomQuota.memoryLimitMB || devCustomQuota.storageGB || devCustomQuota.storageLimitGB || devCustomQuota.cpuLimit);

        const myAppsList = scopedApps;
        const devAppsCount = myAppsList.length;
        const devRunningCount = myAppsList.filter(a => a.status?.toLowerCase().includes('running') || a.status?.toLowerCase().includes('up') || a.status === 'static').length;
        const devTotalMemBytes = myAppsList.reduce((acc, a) => acc + (a.resources?.memUsage || 0), 0);
        const devTotalStorageBytes = myAppsList.reduce((acc, a) => acc + (a.resources?.storageUsage || 0), 0);
        const devTotalCpuPercent = myAppsList.reduce((acc, a) => acc + (parseFloat(a.resources?.cpuPercent) || 0), 0);

        const devAllocDeployments = devCustomQuota.maxDeployments || adminProfile?.limits?.maxDeployments || 3;
        const devAllocMemBytes = devCustomQuota.memoryLimitMB ? (devCustomQuota.memoryLimitMB * 1024 * 1024) : parseMemoryToBytes(adminProfile?.limits?.maxTotalMemory || '1536m');
        const devAllocStorageBytes = (devCustomQuota.storageGB || devCustomQuota.storageLimitGB || adminProfile?.limits?.maxStorageGB || 5) * 1024 * 1024 * 1024;
        const devAllocCpu = devCustomQuota.cpuLimit || adminProfile?.limits?.maxTotalCpu || 1.5;

        const devRemDeployments = Math.max(0, devAllocDeployments - devAppsCount);
        const devRemMem = Math.max(0, devAllocMemBytes - devTotalMemBytes);
        const devRemStorage = Math.max(0, devAllocStorageBytes - devTotalStorageBytes);

        return (
          <div className="dashboard-stats-wrapper fade-in-up">
            {/* Header */}
            <div className="dashboard-stats-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div className="dashboard-stats-header-info">
                <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <Activity size={22} color="var(--primary)" />
                  Resource Usage & Quotas
                </h2>
                <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                  Live application resource utilization and your allocated workspace quotas
                </p>
              </div>
              <button onClick={loadData} className="premium-refresh-btn" title="Refresh resource metrics" style={{ height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <RotateCw size={15} />
              </button>
            </div>

            {/* Allocated Quota Banner */}
            <div style={{
              background: hasDevCustomQuota ? 'rgba(16, 185, 129, 0.08)' : 'rgba(59, 130, 246, 0.08)',
              border: `1px solid ${hasDevCustomQuota ? 'rgba(16, 185, 129, 0.25)' : 'rgba(59, 130, 246, 0.25)'}`,
              borderRadius: '12px',
              padding: '1.15rem 1.4rem',
              marginBottom: '1.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  background: hasDevCustomQuota ? 'rgba(16, 185, 129, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                  color: hasDevCustomQuota ? '#10b981' : '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <CheckCircle size={22} />
                </span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span>{hasDevCustomQuota ? 'Your Allocated Quota:' : 'Shared Plan Quota:'}</span>
                    <span style={{ color: hasDevCustomQuota ? '#10b981' : '#3b82f6' }}>
                      {hasDevCustomQuota
                        ? [
                            devCustomQuota.maxDeployments ? `${devCustomQuota.maxDeployments} Deployments` : null,
                            devCustomQuota.memoryLimitMB ? `${devCustomQuota.memoryLimitMB >= 1024 ? (devCustomQuota.memoryLimitMB / 1024).toFixed(1) + 'GB' : devCustomQuota.memoryLimitMB + 'MB'} RAM` : null,
                            (devCustomQuota.storageGB || devCustomQuota.storageLimitGB) ? `${devCustomQuota.storageGB || devCustomQuota.storageLimitGB}GB Disk` : null,
                            devCustomQuota.cpuLimit ? `${devCustomQuota.cpuLimit} CPU Cores` : null
                          ].filter(Boolean).join(', ')
                        : 'Using shared organization resource pool'}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                    {hasDevCustomQuota
                      ? 'Assigned by your organization Administrator. Deployments are bounded within these limits.'
                      : 'Your account shares the organization pool allocated by your Administrator.'}
                  </div>
                </div>
              </div>
              <span style={{
                fontSize: '0.78rem',
                background: hasDevCustomQuota ? 'rgba(16, 185, 129, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                color: hasDevCustomQuota ? '#10b981' : '#3b82f6',
                padding: '0.35rem 0.75rem',
                borderRadius: '20px',
                fontWeight: 600,
                border: `1px solid ${hasDevCustomQuota ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
              }}>
                ● {hasDevCustomQuota ? 'Custom Allocated Policy' : 'Organization Shared Pool'}
              </span>
            </div>

            {/* Developer's 4 Summary Cards */}
            <div className="stats-grid premium-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              {/* Deployments Card */}
              <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                    <Server size={16} color="#3b82f6" /> Deployments (Apps)
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6' }}>Allocated</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                  {devAppsCount} / {devAllocDeployments}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Used: {devAppsCount}</span>
                  <span style={{ color: devRemDeployments === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                    {devRemDeployments} Remaining
                  </span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (devAppsCount / (devAllocDeployments || 1)) * 100)}%`,
                    height: '100%',
                    background: (devAppsCount / (devAllocDeployments || 1)) > 0.85 ? 'var(--danger)' : '#3b82f6',
                    borderRadius: '4px',
                    transition: 'width 0.4s ease'
                  }} />
                </div>
              </div>

              {/* Memory (RAM) Card */}
              <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                    <Database size={16} color="#a855f7" /> Memory (RAM)
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#a855f7' }}>Allocated</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                  {formatBytesValue(devTotalMemBytes)} / {formatBytesValue(devAllocMemBytes)}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Used: {formatBytesValue(devTotalMemBytes)}</span>
                  <span style={{ color: devRemMem === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                    {formatBytesValue(devRemMem)} Remaining
                  </span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (devTotalMemBytes / (devAllocMemBytes || 1)) * 100)}%`,
                    height: '100%',
                    background: (devTotalMemBytes / (devAllocMemBytes || 1)) > 0.85 ? 'var(--danger)' : '#a855f7',
                    borderRadius: '4px',
                    transition: 'width 0.4s ease'
                  }} />
                </div>
              </div>

              {/* Storage (Disk) Card */}
              <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                    <HardDrive size={16} color="#06b6d4" /> Disk Storage
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#06b6d4' }}>Allocated</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                  {formatBytesValue(devTotalStorageBytes)} / {formatBytesValue(devAllocStorageBytes)}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Used: {formatBytesValue(devTotalStorageBytes)}</span>
                  <span style={{ color: devRemStorage === 0 ? 'var(--danger)' : '#10b981', fontWeight: 600 }}>
                    {formatBytesValue(devRemStorage)} Remaining
                  </span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (devTotalStorageBytes / (devAllocStorageBytes || 1)) * 100)}%`,
                    height: '100%',
                    background: (devTotalStorageBytes / (devAllocStorageBytes || 1)) > 0.85 ? 'var(--danger)' : '#06b6d4',
                    borderRadius: '4px',
                    transition: 'width 0.4s ease'
                  }} />
                </div>
              </div>

              {/* CPU Cores Card */}
              <div className="stat-card-premium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600 }}>
                    <Activity size={16} color="#f59e0b" /> CPU Cores
                  </span>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f59e0b' }}>Utilization</span>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)', margin: '0.4rem 0' }}>
                  {devTotalCpuPercent.toFixed(1)}% / {devAllocCpu} Cores
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.65rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Active Containers: {devRunningCount}</span>
                  <span style={{ color: 'var(--text-muted)' }}>Max CPU limit</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (devTotalCpuPercent / ((devAllocCpu || 1) * 100)) * 100)}%`,
                    height: '100%',
                    background: (devTotalCpuPercent / ((devAllocCpu || 1) * 100)) > 0.85 ? 'var(--danger)' : '#f59e0b',
                    borderRadius: '4px',
                    transition: 'width 0.4s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Developer's Deployed Applications Table */}
            <div className="card" style={{ padding: '1.5rem', borderRadius: '14px', background: 'var(--surface)', border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                  <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(var(--primary-rgb), 0.12)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Server size={19} />
                  </span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-main)' }}>Your Applications</h3>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Resource consumption per deployed application</div>
                  </div>
                </div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                  {myAppsList.length} Total Application(s)
                </span>
              </div>

              {myAppsList.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Server size={36} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', color: 'var(--text-main)' }}>No deployed applications yet</div>
                  <div style={{ fontSize: '0.88rem', marginTop: '0.35rem' }}>Deploy an application to start monitoring its resource consumption.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '650px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface-2, rgba(255,255,255,0.02))' }}>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Application</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>RAM Used</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Storage Used</th>
                        <th style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>CPU Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myAppsList.map((app) => {
                        const isRunning = app.status?.toLowerCase().includes('running') || app.status?.toLowerCase().includes('up') || app.status === 'static';
                        return (
                          <tr key={app.appName} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s ease' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover, rgba(255,255,255,0.02))'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: '0.92rem' }}>
                                {app.appName}
                              </div>
                              {app.domain ? (
                                <a href={`http://${app.domain}`} target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: 'var(--primary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.15rem' }}>
                                  {app.domain} <ExternalLink size={11} />
                                </a>
                              ) : app.port ? (
                                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.15rem', display: 'block' }}>
                                  Port: {app.port}
                                </span>
                              ) : null}
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <span style={{
                                fontSize: '0.75rem',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '12px',
                                background: isRunning ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                color: isRunning ? '#10b981' : 'var(--danger)',
                                fontWeight: 700,
                                textTransform: 'capitalize'
                              }}>
                                ● {app.status || 'unknown'}
                              </span>
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle', fontSize: '0.85rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                              {app.type || 'Docker'}
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                              {formatBytesValue(app.resources?.memUsage || 0)}
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                              {formatBytesValue(app.resources?.storageUsage || 0)}
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                              {parseFloat(app.resources?.cpuPercent || 0).toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {activeTab === 'deployments' && (
        <div className="dashboard-deployments-wrapper fade-in-up">
            <div className="dashboard-section">
              {(Object.keys(apps).length > 0 || scopedApps.length > 0) && !embeddedDeployActiveTab && !selectedDeploymentPath && (
                  <div className="dashboard-controls-section unified-controls">
                    <div className="dashboard-search-wrapper">
                      <div className="dashboard-search-icon-box"><Search size={18} strokeWidth={2.5} /></div>
                      <input
                        type="text"
                        className="dashboard-search-input"
                        placeholder="Search apps..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button className="dashboard-search-clear-btn" onClick={() => setSearchTerm('')} title="Clear search">
                          <X size={14} strokeWidth={3} />
                        </button>
                      )}
                    </div>

                    {/* Team Member Filter Dropdown for Admin */}
                    {user && (user.role === 'admin' || user.isOwner) && (
                      <div className="dashboard-member-filter-wrapper" style={{ margin: 0 }}>
                        <select
                          value={memberFilter}
                          onChange={(e) => setMemberFilter(e.target.value)}
                          style={{
                            height: '42px',
                            padding: '0 2.2rem 0 1rem',
                            borderRadius: '10px',
                            border: '1px solid var(--border)',
                            background: 'var(--surface)',
                            color: 'var(--text-main)',
                            fontSize: '0.88rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            outline: 'none',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 0.75rem center'
                          }}
                        >
                          <option value="all">👥 All Team Deployments ({Object.keys(apps).length})</option>
                          <option value="mine">👤 My Deployments ({Object.values(apps).filter(a => a.owner === user.username).length})</option>
                          {(() => {
                            const seen = new Set([user.username]);
                            const options = [];
                            (teamMembers || []).forEach(tm => {
                              if (!seen.has(tm.username)) {
                                seen.add(tm.username);
                                const cnt = Object.values(apps).filter(a => a.owner === tm.username).length;
                                options.push(
                                  <option key={tm.username} value={`@${tm.username}`}>
                                    @{tm.username} ({cnt})
                                  </option>
                                );
                              }
                            });
                            (developers || []).forEach(d => {
                              if (!seen.has(d)) {
                                seen.add(d);
                                const cnt = Object.values(apps).filter(a => a.owner === d).length;
                                options.push(
                                  <option key={d} value={`@${d}`}>
                                    @{d} ({cnt})
                                  </option>
                                );
                              }
                            });
                            return options;
                          })()}
                        </select>
                      </div>
                    )}

                    <div className="dashboard-filter-group">
                      <div className="dashboard-filter-tabs">
                        <button className={`filter-tab ${filterType === 'all' ? 'active' : ''}`} onClick={() => setFilterType('all')}>
                          All <span className="filter-count">{scopedApps.length}</span>
                        </button>
                        <button className={`filter-tab ${filterType === 'docker' ? 'active' : ''}`} onClick={() => setFilterType('docker')}>
                          Docker <span className="filter-count">{scopedDockerCount}</span>
                        </button>
                        <button className={`filter-tab ${filterType === 'zip' ? 'active' : ''}`} onClick={() => setFilterType('zip')}>
                          Static <span className="filter-count">{scopedStaticCount}</span>
                        </button>
                      </div>

                      <button
                        onClick={loadData}
                        className="premium-refresh-btn"
                        title="Refresh Deployments"
                      >
                        <RotateCw size={16} />
                      </button>
                    </div>
                  </div>
              )}

              {selectedDeploymentPath ? (
                <DeploymentDetailPage
                  path={selectedDeploymentPath}
                  appInfo={apps[selectedDeploymentPath] || {}}
                  appName={selectedDeploymentPath.split('/').pop()}
                  ownerName={(apps[selectedDeploymentPath] || {}).owner || 'Unknown'}
                  onBack={() => setSelectedDeploymentPath(null)}
                  user={user}
                  actionLoading={actionLoading}
                  handleContainerAction={handleContainerAction}
                  handleDeleteApp={handleDeleteApp}
                  handleRebuild={handleRebuild}
                  handleCreateSnapshot={handleCreateSnapshot}
                  handleDownloadSnapshot={handleDownloadSnapshot}
                  openGlobalWorkspace={openGlobalWorkspace}
                  setTerminalApp={setTerminalApp}
                  setShowTerminal={setShowTerminal}
                  setEditingAppConfig={setEditingAppConfig}
                  setEditingFileData={setEditingFileData}
                  setFileBrowser={setFileBrowser}
                  setShowFileEditor={setShowFileEditor}
                  loadFileBrowser={loadFileBrowser}
                  handleCopyLink={handleCopyLink}
                  url={(apps[selectedDeploymentPath] || {}).domain ? `http://${(apps[selectedDeploymentPath] || {}).domain}/` : ((apps[selectedDeploymentPath] || {}).port ? `http://${window.location.hostname}:${(apps[selectedDeploymentPath] || {}).port}/` : null)}
                  onStatusMessage={onStatusMessage}
                  deploymentSnapshot={deploymentLogSnapshots[selectedDeploymentPath] || (((apps[selectedDeploymentPath] || {}).metadata?.deploymentId) ? {
                    deploymentId: apps[selectedDeploymentPath].metadata.deploymentId,
                    status: apps[selectedDeploymentPath].metadata.deploymentStatus || 'completed',
                    logs: Array.isArray(apps[selectedDeploymentPath].metadata.deploymentLogs) ? apps[selectedDeploymentPath].metadata.deploymentLogs : [],
                    source: 'metadata'
                  } : null)}
                  deploymentRuntimeLogs={deploymentRuntimeLogs}
                  deploymentRuntimeLoading={deploymentRuntimeLoading}
                  storageBytes={(apps[selectedDeploymentPath] || {}).resources?.storageUsage || 0}
                />
              ) : user && (user.role === 'developer' || user.role === 'admin') && embeddedDeployActiveTab ? (
                <div style={{ maxWidth: '1350px', margin: '0 auto' }}>
                  <div className="dashboard-embedded-deploy-frame">
                    <div className="dashboard-embedded-deploy" style={{ textAlign: 'left' }}>
                      <Deploy
                        user={user}
                        onStatusMessage={onStatusMessage}
                        onConfirm={onConfirm}
                        initialTab={embeddedDeployActiveTab || null}
                        onTabChange={(tab) => {
                          setEmbeddedDeployActiveTab(tab && tab !== 'deployments' ? tab : null);
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : appEntries.length > 0 ? (
                <div className="dashboard-deployments-list-wrapper" style={{ padding: '0', overflow: 'visible' }}>
                  <div className="apps-list">
                    {appEntries.map(([path, appInfo]) => {
                      const appName = appInfo.appName || path.split('/').pop();
                      const ownerName = appInfo.owner || 'Unknown';
                      const isContainerized = appInfo.type === 'docker' || appInfo.type === 'compose';
                      const hasRuntimeLogs = isContainerized || appInfo.metadata?.isSSR || appInfo.metadata?.servedBy === 'docker' || appInfo.metadata?.servedBy === 'pm2';
                      const isCompose = appInfo.type === 'compose';
                      const isRepo = appInfo.source === 'repo' || appInfo.metadata?.source === 'repo';
                      const status = appInfo.status || 'unknown';
                      const isRunning = status === 'running';
                      const url = appInfo.domain ? `http://${appInfo.domain}/` : (appInfo.port ? `http://${window.location.hostname}:${appInfo.port}/` : null);
                      const loadingKey = actionLoading[path] || actionLoading[appName];
                      const deploymentSnapshot = deploymentLogSnapshots[path] || (appInfo.metadata?.deploymentId ? {
                        deploymentId: appInfo.metadata.deploymentId,
                        status: appInfo.metadata.deploymentStatus || 'completed',
                        logs: Array.isArray(appInfo.metadata.deploymentLogs) ? appInfo.metadata.deploymentLogs : [],
                        source: 'metadata'
                      } : null);
                      const storageBytes = (appInfo.resources?.storageUsage || 0);
                      return (
                        <DeploymentCard 
                          key={path}
                          path={path}
                          appInfo={appInfo}
                          user={user}
                          onClick={(p) => {
                             // Will implement modal open logic here later
                             setSelectedDeploymentPath(p);
                          }} 
                        />
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="premium-empty-container">
                  <div className="premium-empty-glow-bg" />
                  
                  {searchTerm || filterType !== 'all' || memberFilter !== 'all' ? (
                    <>
                      <div className="dashboard-empty-icon" style={{ opacity: 0.4, marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><Search size={52} /></div>
                      <h3 className="premium-empty-title" style={{ fontSize: '1.5rem' }}>No deployments match your criteria</h3>
                      <p className="premium-empty-subtitle" style={{ marginBottom: '1.5rem' }}>
                        Try adjusting your search query or clear active filters to view all projects.
                      </p>
                      <button 
                        className="btn-secondary"
                        onClick={() => { setSearchTerm(''); setFilterType('all'); setMemberFilter('all'); }}
                        style={{ padding: '0.6rem 1.25rem', borderRadius: '10px', fontWeight: 600 }}
                      >
                        Reset Search Filters
                      </button>
                    </>
                  ) : (
                    <div className="vercel-empty-state-container">
                      <div className="vercel-empty-icon-wrapper">
                        <UploadCloud size={28} />
                      </div>
                      
                      <h2 className="vercel-empty-title">Deploy your first project</h2>
                      
                      <p className="vercel-empty-subtitle">
                        Start with a template, import a project,<br/>
                        or drop a file, folder, or .zip to deploy.
                      </p>

                      <div className="vercel-empty-list-card">
                        <div className="vercel-empty-list-item" onClick={() => { handleStartDeploy('repo'); }}>
                          <div className="vercel-empty-list-icon"><Github size={18} /></div>
                          <div className="vercel-empty-list-info">
                            <h3>Import from GitHub / GitLab</h3>
                            <p>Clone repository, run frontend build, and deploy static output.</p>
                          </div>
                          <button className="vercel-empty-btn">Import</button>
                        </div>

                        <div className="vercel-empty-list-item" onClick={() => { handleStartDeploy('hub'); }}>
                          <div className="vercel-empty-list-icon"><Cloud size={18} /></div>
                          <div className="vercel-empty-list-info">
                            <h3>Docker Hub Registry</h3>
                            <p>Deploy directly from a container registry.</p>
                          </div>
                          <button className="vercel-empty-btn">Deploy</button>
                        </div>

                        <div className="vercel-empty-list-item" onClick={() => { handleStartDeploy('docker'); }}>
                          <div className="vercel-empty-list-icon"><Package size={18} /></div>
                          <div className="vercel-empty-list-info">
                            <h3>Docker Image</h3>
                            <p>Deploy using a local Docker image tarball.</p>
                          </div>
                          <button className="vercel-empty-btn">Deploy</button>
                        </div>

                        <div className="vercel-empty-list-item" onClick={() => { handleStartDeploy('compose'); }}>
                          <div className="vercel-empty-list-icon"><Server size={18} /></div>
                          <div className="vercel-empty-list-info">
                            <h3>Docker Compose Workspace</h3>
                            <p>Full DinD environment with terminal access.</p>
                          </div>
                          <button className="vercel-empty-btn">Deploy</button>
                        </div>

                        <div className="vercel-empty-list-item" onClick={() => { handleStartDeploy('zip'); }}>
                          <div className="vercel-empty-list-icon"><FolderOpen size={18} /></div>
                          <div className="vercel-empty-list-info">
                            <h3>ZIP Archive</h3>
                            <p>Upload a file, folder, or .zip to deploy.</p>
                          </div>
                          <button className="vercel-empty-btn">Deploy</button>
                        </div>
                      </div>

                      <div className="vercel-empty-browse-card" onClick={() => { handleStartDeploy('template'); }}>
                        <div className="vercel-empty-browse-info">
                          <h3>Browse Templates</h3>
                          <p>Jumpstart AI projects, ecommerce, and more</p>
                        </div>
                        <div className="vercel-empty-browse-right">
                          <div className="vercel-empty-framework-icons">
                            <LayoutTemplate size={16} />
                            <Code size={16} />
                            <Database size={16} />
                          </div>
                          <div className="vercel-empty-external-icon">
                            <ExternalLink size={14} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      )}

      {/* Deployment Type Selection Modal */}
      {showDeployTypeModal && (
        <div className="modal-overlay" onClick={() => setShowDeployTypeModal(false)} style={{ zIndex: 3000 }}>
          <div className="premium-deploy-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem' }}>
              <div>
                <h2 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                  Select Deployment Type
                </h2>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Choose how you want to build and host your application
                </p>
              </div>
              <button 
                onClick={() => setShowDeployTypeModal(false)} 
                style={{ 
                  background: 'var(--surface)', 
                  border: '1px solid var(--border)', 
                  color: 'var(--text-muted)', 
                  cursor: 'pointer',
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('hub'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <Cloud size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">Docker Hub</span>
                  <span className="premium-deploy-option-desc">Deploy directly from Docker Hub or a private container registry</span>
                </div>
              </button>

              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('docker'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <Package size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">Docker Image</span>
                  <span className="premium-deploy-option-desc">Deploy using a local Docker image tarball</span>
                </div>
              </button>

              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('compose'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <Server size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">Docker Compose Workspace</span>
                  <span className="premium-deploy-option-desc">Full DinD environment with terminal access & file manager</span>
                </div>
              </button>

              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('repo'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <Code size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">GitHub Repository</span>
                  <span className="premium-deploy-option-desc">Connect public/private Git repository with auto-build</span>
                </div>
              </button>

              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('template'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <Sparkles size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">App Templates</span>
                  <span className="premium-deploy-option-desc">Instant 1-click starters for Node.js, Python, MySQL, Redis & more</span>
                </div>
              </button>

              <button className="premium-deploy-option-card" onClick={() => { handleStartDeploy('zip'); setShowDeployTypeModal(false); }}>
                <div className="premium-deploy-option-icon">
                  <FolderOpen size={24} />
                </div>
                <div className="premium-deploy-option-info">
                  <span className="premium-deploy-option-title">ZIP Archive Upload</span>
                  <span className="premium-deploy-option-desc">Upload source code ZIP for automated stack detection & build</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* File Editor Modal */}
      {showFileEditor && (
        <div className="modal-overlay" onClick={() => {
          if (editingFileData.saving) return;
          if (editingFileData.content !== editingFileData.originalContent) {
            onConfirm(
              'Unsaved Changes',
              'You have unsaved changes. Are you sure you want to close the editor?',
              () => setShowFileEditor(false),
              'warning'
            );
            return;
          }
          setShowFileEditor(false);
        }}>
          <div className="file-editor-modal file-editor-with-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="file-editor-header">
              <div className="file-editor-header-info">
                <div>
                  <h2>Container File Editor</h2>
                  <p>{editingFileData.appName}{editingFileData.filename ? ` • /${editingFileData.filename}` : ''}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button 
                  className="btn-secondary" 
                  onClick={() => setFileEditorShowTerminal(prev => !prev)} 
                  style={{ display: 'none', padding: '0.4rem 0.6rem', border: '1px solid transparent', background: fileEditorShowTerminal ? 'var(--surface-2)' : 'transparent', color: fileEditorShowTerminal ? 'var(--primary)' : 'var(--text-muted)' }}
                  title="Toggle Terminal"
                >
                  <Terminal size={16} />
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={() => setFileAiState(prev => ({ ...prev, showSidebar: !prev.showSidebar }))} 
                  style={{ display: 'none', padding: '0.4rem 0.6rem', border: '1px solid transparent', background: fileAiState.showSidebar ? 'var(--surface-2)' : 'transparent', color: fileAiState.showSidebar ? 'var(--primary)' : 'var(--text-muted)' }}
                  title="Code Agent"
                >
                  <Sparkles size={16} />
                </button>

                {editingFileData.filename && (
                  <button
                    className="btn-secondary"
                    style={{ padding: '0.4rem 0.6rem', border: fileSearch.visible ? '1px solid var(--primary)' : '1px solid transparent', background: fileSearch.visible ? 'var(--surface-2)' : 'transparent', color: 'var(--text-muted)' }}
                    onClick={() => {
                      setFileSearch(prev => ({ ...prev, visible: !prev.visible }));
                      if (!fileSearch.visible && textareaRef.current) {
                        setTimeout(() => {
                          const input = document.getElementById('file-editor-search-input');
                          if (input) input.focus();
                        }, 50);
                      }
                    }}
                    title="Find (Ctrl+F)"
                  >
                    <Search size={16} />
                  </button>
                )}
                
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '0.5rem', borderLeft: '1px solid var(--border)', paddingLeft: '1rem' }}>
                  <button className="btn-secondary" onClick={() => handleSaveFile(false)} disabled={!editingFileData.filename || editingFileData.loading || editingFileData.saving || !!editingFileData.error} style={{ padding: '0.4rem 0.8rem', border: '1px solid var(--border)' }}>
                    <Save size={14} style={{ marginRight: '0.3rem' }} /> {editingFileData.saving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn-primary" onClick={() => handleSaveFile(true)} disabled={!editingFileData.filename || editingFileData.loading || editingFileData.saving || !!editingFileData.error} style={{ padding: '0.4rem 0.8rem' }}>
                    <RotateCw size={14} style={{ marginRight: '0.3rem' }} /> {editingFileData.saving ? 'Saving...' : 'Save & Restart'}
                  </button>
                </div>

                <button className="btn-close" onClick={() => setShowFileEditor(false)} disabled={editingFileData.saving} style={{ marginLeft: '0.5rem' }}><X size={20} /></button>
              </div>
            </div>

            {fileSearch.visible && editingFileData.filename && (
              <div className="file-editor-find-bar" style={{ padding: '0.5rem 2rem', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Search size={14} color="var(--text-tertiary)" />
                <input
                  id="file-editor-search-input"
                  type="text"
                  value={fileSearch.query}
                  onChange={(e) => setFileSearch(prev => ({ ...prev, query: e.target.value, matches: [], currentIndex: -1 }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (fileSearch.matches.length === 0) {
                        executeSearch();
                      } else {
                        traverseFind(e.shiftKey ? -1 : 1);
                      }
                    }
                  }}
                  placeholder="Find in file..."
                  style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none', flex: 1 }}
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600 }}>
                  {fileSearch.matches.length > 0 ? `${fileSearch.currentIndex + 1} of ${fileSearch.matches.length}` : (fileSearch.query ? 'No results' : '')}
                </span>
                <button className="btn-ghost" style={{ padding: '0.2rem', width: '28px', height: '28px', minWidth: 'auto', borderRadius: '6px', color: 'var(--text-muted)' }} onClick={() => traverseFind(-1)} disabled={!fileSearch.query}>↑</button>
                <button className="btn-ghost" style={{ padding: '0.2rem', width: '28px', height: '28px', minWidth: 'auto', borderRadius: '6px', color: 'var(--text-muted)' }} onClick={() => traverseFind(1)} disabled={!fileSearch.query}>↓</button>
              </div>
            )}

            <div className="file-editor-content-area">
              {/* File Tree Sidebar */}
              <div className="file-editor-sidebar">
                <div className="file-tree-header">
                  <Folder size={14} />
                  <span>/</span>
                  {fileBrowser.isContainerOffline && (
                    <span style={{ fontSize: '10px', marginLeft: '6px', background: '#eab30820', color: '#eab308', padding: '2px 6px', borderRadius: '4px', border: '1px solid #eab30830', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <AlertTriangle size={10} />
                      OFFLINE
                    </span>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
                    <button
                      className="file-tree-refresh"
                      onClick={handleCreateFile}
                      title="New file"
                    >
                      <Plus size={12} />
                    </button>
                    <button
                      className="file-tree-refresh"
                      onClick={() => {
                        setFileBrowser(prev => ({ ...prev, dirCache: {}, expandedDirs: {} }));
                        loadFileBrowser(editingFileData.appName, editingFileData.owner, '');
                      }}
                      title="Refresh file tree"
                    >
                      <RotateCw size={12} />
                    </button>
                  </div>
                </div>
                <div className="file-tree-list">
                  {fileBrowser.loading && Object.keys(fileBrowser.dirCache).length === 0 ? (
                    <div className="file-tree-loading">
                      <RotateCw size={14} className="animate-spin" /> Loading...
                    </div>
                  ) : (
                    renderFileTree('')
                  )}
                </div>
              </div>

              {/* Editor Pane (Middle Column) */}
              <div className="file-editor-pane" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="file-editor-body" style={{ flex: '1 1 60%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {!editingFileData.filename ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.75rem', color: 'var(--text-tertiary)', background: 'var(--surface)', textAlign: 'center', padding: '2rem' }}>
                      <Folder size={40} style={{ opacity: 0.3 }} />
                      <div style={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: '1.1rem' }}>Select a file to edit</div>
                      <div style={{ fontSize: '0.85rem', maxWidth: '320px', color: 'var(--text-tertiary)' }}>Browse the file tree on the left and click any file to open it in the editor.</div>
                    </div>
                  ) : editingFileData.loading ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', background: 'var(--surface)' }}>
                      <RotateCw className="animate-spin" style={{ marginRight: '0.5rem' }} /> Loading file content...
                    </div>
                  ) : editingFileData.error ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '0.5rem', color: 'var(--text-tertiary)', background: 'var(--surface)', textAlign: 'center', padding: '1.5rem' }}>
                      <FileCode size={30} style={{ opacity: 0.4 }} />
                      <div style={{ fontWeight: 800, color: 'var(--text-main)' }}>File not available</div>
                      <div style={{ fontSize: '0.85rem', maxWidth: '320px' }}>{editingFileData.error}</div>
                    </div>
                  ) : (
                    <textarea
                      ref={textareaRef}
                      className="file-editor-textarea"
                      value={editingFileData.content}
                      onChange={(e) => {
                        setEditingFileData(prev => ({ ...prev, content: e.target.value }));
                        if (fileSearch.visible && fileSearch.query) {
                          setFileSearch(prev => ({ ...prev, matches: [], currentIndex: -1 }));
                        }
                      }}
                      placeholder={`Enter ${editingFileData.filename} content...`}
                      disabled={editingFileData.saving}
                      spellCheck="false"
                      style={{ height: '100%' }}
                    />
                  )}
                  
                  {/* File Editor Footer Actions Removed - Moved to Header */}
                </div>
                
                {/* Embedded Terminal (Bottom) - HIDDEN TEMPORARILY */}
                {false && fileEditorShowTerminal && (
                  <div className="embedded-terminal-wrapper" style={{ flex: '0 0 35%', minHeight: '200px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
                    <ContainerTerminal 
                      appName={editingFileData.appName} 
                      owner={editingFileData.owner}
                      mode="shell"
                      command="/bin/sh"
                      onClose={() => setFileEditorShowTerminal(false)}
                      onStatusMessage={onStatusMessage}
                      onOpenFileManager={() => {
                        // In embedded mode we just close the terminal so they see the file editor.
                        setFileEditorShowTerminal(false);
                      }}
                      embedded={true}
                    />
                  </div>
                )}
              </div>

              {/* Agentic AI (Right Sidebar) - HIDDEN TEMPORARILY */}
              {false && fileAiState.showSidebar && (
                <div className="file-editor-ai-sidebar" style={{ width: '320px', borderLeft: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: 'var(--primary)' }}>
                      <Sparkles size={16} /> Code Agent
                    </div>
                    <button onClick={() => setFileAiState(prev => ({ ...prev, showSidebar: false }))} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                      <X size={16} />
                    </button>
                  </div>
                  
                  <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {fileAiState.messages.length === 0 && !fileAiState.loading && (
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '2rem' }}>
                        <Sparkles size={32} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                        <p>Ask me to edit this file.</p>
                        <p style={{ marginTop: '0.5rem', color: 'var(--text-tertiary)' }}>Example: "Refactor this component to use hooks" or "Add error handling to this function."</p>
                      </div>
                    )}
                    {fileAiState.messages.map((msg, idx) => (
                      <div key={idx} style={{ 
                        padding: '0.75rem 1rem', 
                        borderRadius: '8px', 
                        fontSize: '0.85rem',
                        lineHeight: 1.5,
                        background: msg.role === 'user' ? 'var(--primary)' : msg.role === 'error' ? 'var(--danger)' : 'var(--surface)',
                        color: msg.role === 'user' ? '#ffffff' : msg.role === 'error' ? '#ffffff' : 'var(--text-main)',
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '92%',
                        border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        {msg.content}
                      </div>
                    ))}
                    {fileAiState.loading && (
                      <div style={{ alignSelf: 'flex-start', padding: '0.75rem', borderRadius: '8px', fontSize: '0.85rem', background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <RotateCw size={14} className="animate-spin" /> Thinking...
                      </div>
                    )}
                  </div>
                  
                  <div style={{ padding: '0.75rem', borderTop: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', gap: '0.5rem' }}>
                    <input 
                      type="text" 
                      value={fileAiState.prompt}
                      onChange={(e) => setFileAiState(prev => ({ ...prev, prompt: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleFileAiAssist();
                      }}
                      placeholder={editingFileData.filename ? "Instruct AI..." : "Select a file first..."}
                      style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none' }}
                      disabled={fileAiState.loading || !editingFileData.filename}
                    />
                    <button 
                      className="btn-primary" 
                      onClick={handleFileAiAssist} 
                      disabled={fileAiState.loading || !fileAiState.prompt.trim() || !editingFileData.filename}
                      style={{ padding: '0.5rem', minWidth: '40px' }}
                    >
                      {fileAiState.loading ? <RotateCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}
        
      </div>
      
      {/* App Config Modal */}
      {editingAppConfig && (
        <div className="modal-overlay" onClick={() => setEditingAppConfig(null)}>
          <div className="terminal-modal-container" style={{ maxWidth: '500px', height: 'auto', maxHeight: '90vh', background: 'var(--surface-2)', padding: '2rem', borderRadius: '12px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Settings size={20} /> Edit {editingAppConfig.appName}</h2>
              <button className="btn-close" onClick={() => setEditingAppConfig(null)}><X size={20} /></button>
            </div>
            
            <form onSubmit={saveAppConfig} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Deployment Name (Display)</label>
                <input 
                  type="text" 
                  className="form-control" 
                  value={editingAppConfig.meta.displayName !== undefined ? editingAppConfig.meta.displayName : editingAppConfig.appName} 
                  onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, displayName: e.target.value } }))} 
                  placeholder="e.g. My Awesome App"
                />
              </div>

              {(editingAppConfig.meta.type === 'docker' || editingAppConfig.meta.type === 'compose') && (
                <>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Internal Port</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={editingAppConfig.meta.internalPort || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, internalPort: e.target.value } }))} 
                      placeholder="e.g. 3000"
                    />
                    <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.25rem' }}>The port the application listens on inside the container.</small>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Memory Limit</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingAppConfig.meta.memory || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, memory: e.target.value } }))} 
                      placeholder="e.g. 512m, 1g"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>CPU Limit</label>
                    <input 
                      type="number" 
                      step="0.1" 
                      className="form-control" 
                      value={editingAppConfig.meta.cpus || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, cpus: e.target.value } }))} 
                      placeholder="e.g. 0.5, 1.0"
                    />
                  </div>
                </>
              )}

              {editingAppConfig.meta.type === 'zip' && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Port</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={editingAppConfig.meta.internalPort || editingAppConfig.meta.port || ''} 
                    onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, internalPort: parseInt(e.target.value) || e.target.value } }))} 
                    placeholder="e.g. 3000"
                  />
                  <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '0.25rem' }}>The port the static server runs on.</small>
                </div>
              )}

              {editingAppConfig.meta.source === 'repo' && (
                <>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Build Command</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingAppConfig.meta.buildCommand || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, buildCommand: e.target.value } }))} 
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Install Command</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingAppConfig.meta.installCommand || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, installCommand: e.target.value } }))} 
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Output Directory</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingAppConfig.meta.outputDir || ''} 
                      onChange={e => setEditingAppConfig(prev => ({ ...prev, meta: { ...prev.meta, outputDir: e.target.value } }))} 
                    />
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingAppConfig(null)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Container Terminal Modal */}
      
      

      {showTerminal && createPortal(
        <div className="modal-overlay" onClick={() => setShowTerminal(false)}>
          <div className="terminal-modal-container" onClick={(e) => e.stopPropagation()}>
            <ContainerTerminal
              appName={terminalApp.appName}
              owner={terminalApp.owner}
              mode={terminalApp.mode}
              command={terminalApp.command}
              sshConfig={terminalApp.sshConfig}
              token={localStorage.getItem('token')}
              onClose={() => setShowTerminal(false)}
              onStatusMessage={onStatusMessage}
              onOpenFileManager={() => {
                setShowTerminal(false);
                setEditingFileData({ appName: terminalApp.appName, owner: terminalApp.owner, filename: '', content: '', originalContent: '', loading: false, saving: false, error: null });
                setFileBrowser({ entries: [], loading: false, currentDir: '', expandedDirs: {}, dirCache: {} });
                setShowFileEditor(true);
              }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* ComposeWorkspaceModal removed and moved to App.js */}
    </>
  );
}

export default Dashboard;
